import { TokenResponse } from 'expo-auth-session';
import { Href, router } from 'expo-router';
import React, { useContext, createContext, type PropsWithChildren, useEffect, useRef, useState } from 'react';

import { apiService } from '@/services/api-service';
import { authService, type UserSession } from '@/services/auth-service';
import type { User } from '@/types/models';
import { logger } from '@/utils/logger';
import { queryClient } from '@/utils/query-client';

export type { UserSession } from '@/services/auth-service';


const AuthContext = createContext<{
	signIn: (tokens?: TokenResponse, redirectTo?: Href) => void;
	updateSession: (session: UserSession) => void;
	refreshTokens: () => Promise<UserSession | null>;
	signOut: () => void;
	session?: UserSession | null;
	tokens?: TokenResponse | null;
	isLoading: boolean;
	authError: string | null;
	setAuthError: (error: string | null) => void;
	// Add user data to context
	user: User | null;
	isUserLoading: boolean;
		}>({
			signIn: () => null,
			updateSession: () => null,
			refreshTokens: () => Promise.resolve(null),
			signOut: () => null,
			session: null,
			isLoading: false,
			authError: null,
			setAuthError: () => null,
			user: null,
			isUserLoading: false,
		});

// This hook can be used to access the user info.
export function useSession() {
	const value = useContext(AuthContext);
	if (process.env.NODE_ENV !== 'production') {
		if (!value) {
			throw new Error('useSession must be wrapped in a <SessionProvider />');
		}
	}

	return value;
}

export const SessionProvider = ({ children }: PropsWithChildren) => {
	const [session, setSession] = useState<UserSession | null>(null);
	const [authError, setAuthError] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [user, setUser] = useState<User | null>(null);
	const [isUserLoading, setIsUserLoading] = useState(true);
	const previousSessionKeyRef = useRef<string | null>(null);

	useEffect(() => {
		let cancelled = false;

		// Initialize with auth service data
		const initializeSession = async () => {
			try {
				await authService.waitForInitialization();
				if (cancelled) return;

				const currentSession = await authService.getSession();
				const currentError = authService.getAuthError();

				if (cancelled) return;

				setSession(currentSession);
				setAuthError(currentError);
				setIsLoading(false);

				// User data will be loaded by the API service session change handler
				// We just need to get the current user if it's already been loaded
				if (currentSession) {
					const currentUser = await authService.getUser();
					if (cancelled) return;
					setUser(currentUser);

					// If no user data yet, the API service will load it via session change handler
					if (!currentUser) {
						// Don't set isUserLoading to false yet, let the user change listener handle it
						return;
					}
				}

				setIsUserLoading(false);
			} catch (error) {
				if (cancelled) return;
				console.error('Error initializing session:', error);
				setIsLoading(false);
				setIsUserLoading(false);
			}
		};

		initializeSession();

		// Set up listeners for auth service changes
		const unsubscribeSession = authService.onSessionChange((newSession) => {
			logger.log('SessionProvider: session changed', newSession?.email);

			// Prevent leaking cached data between demo ↔️ production or between users.
			// We key by user id + demo flag, but ignore token refresh/device-token updates.
			const nextSessionKey = newSession ? `${newSession.id}:${newSession.isDemoMode ? 'demo' : 'remote'}` : null;
			if (previousSessionKeyRef.current !== nextSessionKey) {
				logger.log('SessionProvider: session identity changed, clearing query cache', {
					from: previousSessionKeyRef.current,
					to: nextSessionKey,
				});
				queryClient.clear();
				previousSessionKeyRef.current = nextSessionKey;
			}

			setSession(newSession);
			// When session changes, user data may also change, so set user loading
			if (newSession) {
				setIsUserLoading(true);
			} else {
				setUser(null);
				setIsUserLoading(false);
			}
		});

		const unsubscribeError = authService.onAuthError((error) => {
			logger.log('SessionProvider: auth error changed', error);
			setAuthError(error);
		});

		// Set up listener for user data changes
		const unsubscribeUser = apiService.onUserChange((newUser) => {
			logger.log('SessionProvider: user changed', newUser);
			setUser(newUser);
			setIsUserLoading(false);
		});

		return () => {
			cancelled = true;
			unsubscribeSession();
			unsubscribeError();
			unsubscribeUser();
		};
	}, []);

	// Safety timeout: only fire while we actually have a session and are
	// waiting for user data. Previously this ran unconditionally on mount
	// and logged a warning every 10s for every unauthenticated view.
	useEffect(() => {
		if (!session || !isUserLoading) return;
		const timeoutId = setTimeout(() => {
			console.warn('SessionProvider: User loading timeout, continuing without user data');
			setIsUserLoading(false);
		}, 10000);
		return () => clearTimeout(timeoutId);
	}, [session, isUserLoading]);

	return (
		<AuthContext.Provider
			value={{
				signIn: async (tokens?: TokenResponse, redirectTo?: Href) => {
					try {
						logger.log('SessionProvider: Starting sign-in process', {
							hasTokens: !!tokens,
							redirectTo,
						});

						let session;
						if (tokens) {
							session = await authService.signIn(tokens);
						} else {
							console.warn('No tokens provided for sign-in');
							authService.signOut();
							return;
						}

						logger.log('SessionProvider: Sign-in completed', {
							hasSession: !!session,
							sessionId: session?.id,
						});

						if (session && redirectTo) {
							logger.log('SessionProvider: Redirecting to', redirectTo);
							router.replace(redirectTo);
						}
					} catch (error) {
						console.error('SessionProvider signIn error:', error);
					}
				},
				signOut: async () => {
					try {
						await authService.signOut();
						// Clear user state
						setUser(null);
						setIsUserLoading(false);
					} catch (error) {
						console.error('SessionProvider signOut error:', error);
					}
				},
				refreshTokens: async () => {
					try {
						return await authService.refreshTokens();
					} catch (error) {
						console.error('SessionProvider refreshTokens error:', error);
						return null;
					}
				},
				updateSession: (newSession: UserSession) => {
					// This is for backward compatibility - not commonly used
					setSession(newSession);
				},
				session,
				tokens: null, // We don't expose raw tokens anymore, they're handled by auth service
				isLoading,
				authError,
				setAuthError: (error: string | null) => {
					authService.setAuthError(error);
				},
				user,
				isUserLoading,
			}}>
			{children}
		</AuthContext.Provider>
	);
};
